/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @typescript-eslint/no-shadow */
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  RadioGroup,
} from '@mui/material'
import { Form, FormikProvider, useFormik } from 'formik'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import * as Yup from 'yup'
import Link from 'next/link'
import registrationPageCharacter from '@asset/game/registration/registration-character.webp'
import useRegister from '@hook/user/useRegister'
import { ValidationMessage } from '@type/validation'
import { PROJECT } from 'configs'
import {
  BoxCharacterMainWrapper,
  BoxRegistrationFormTitleMainWrapper,
  BoxRegistrationFormfooterMainWrapper,
  BoxRegistrationMainWrapper,
  BoxRegistrationSubWrapper,
  ButtonRegistrationFormSignUp,
  CustomRadio,
  FormControlLabelRegistrationForm,
  FormFieldsContainerBox,
  FormHelperTextRegistrationForm,
  ImageContainerBox,
  Label,
  LoginLink,
  NormalLabel,
  SignUpImageChar,
  TextFieldRegistrationForm,
  TypographyAdventurePacks,
  TypographyAdventurePacksInfo,
  TypographyAgeVerification,
  TypographyRegistrationFormSubTitle,
  TypographyRegistrationFormTitle,
  TypographyRegistrationFormTitleWrapper,
  TypographyRegistrationFormfooterSubTitle,
  TypographyRegistrationFormfooterTitle,
} from '@template/RegisterPage/Register.Styled'
import theme from '@theme'
import CheckboxControl from '@ui/CheckboxControl'
import LegalLinks from '@constant/LegalLinks'

const RegisterForm = () => {
  const [showPassword, setShowPassword] = useState(false)
  const { registerUser } = useRegister({ onError: onRegisterError })
  const router = useRouter()
  const referrerGamerTag = router.query.referrerGamerTag as string
  const [ageCheck, setAgeCheck] = useState<boolean>(true)
  const [isClickDisabled, setIsClickDisabled] = useState(false)
  const formik = useFormik({
    initialValues: {
      username: '',
      email: '',
      password: '',
      ageOver13: null,
      subscribedForAdventurePacks: true,
    },
    validationSchema: Yup.object().shape({
      username: Yup.string()
        .nullable()
        .required('GamerTag is required')
        .trim()
        .matches(
          /^[^\s]{6,}$/,
          'GamerTag must be at least 6 characters consisting of letters, numbers, period, symbols or underscore, no blank spaces allowed.',
        ),
      email: Yup.string()
        .email('Email must be a valid email address')
        .required('Email is required.'),
      password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('Password is required'),
      ageOver13: Yup.boolean().nullable().required('Age confirmation is required'),
    }),
    onSubmit: async (values) => {
      if (values.ageOver13 === 'false' || values.ageOver13 === null) {
        toast.error('Sorry, you must be 14 years or older')
      } else {
        const { ...regUserData } = values
        await registerUser({
          ...regUserData,
          loginProvider: 'email',
        })
      }
    },
  })

  useEffect(
    () => {
      if (referrerGamerTag) {
        formik.setValues((values) => ({
          ...values,
          referrerGamerTag,
        }))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [referrerGamerTag],
  )

  function onRegisterError(axiosError: AxiosError) {
    const error = axiosError?.response?.data
    if (typeof error === 'object' && 'message' in error) {
      if (Array.isArray(error?.message)) {
        const validationErrors = error?.message?.reduce(
          (accumulator, message: ValidationMessage) => {
            if (message?.property in formik.values) {
              const field = message?.property
              if (field) {
                accumulator[field] = Object.values(message?.constraints ?? {})?.join(' ') ?? ''
              }
            }
            return accumulator
          },
          {},
        )
        if (Object.keys(validationErrors ?? {})?.length > 0) {
          formik.setErrors(validationErrors)
        } else {
          toast.error('Something went wrong during registration...')
        }
      } else {
        toast.error((error?.message as string) ?? 'Something went wrong during registration...')
      }
    }
  }
  const handleAgeCheckClick = () => {
    if (!isClickDisabled) {
      setAgeCheck(true)
      toast.error('Sorry, you must be 14 years or older')
      setIsClickDisabled(true)
    }
  }

  const handleSubscribedClick = (checked: boolean) => {
    formik.setFieldValue('subscribedForAdventurePacks', checked)
  }

  const { errors, touched, handleSubmit, getFieldProps, isSubmitting, isValid, dirty } = formik

  return (
    <BoxRegistrationMainWrapper>
      <BoxRegistrationSubWrapper>
        <TypographyRegistrationFormTitle>
          Our Political System No Longer Works For Us.
        </TypographyRegistrationFormTitle>
        <Box sx={{ mt: 6 }}>
          <TypographyRegistrationFormSubTitle>
            We Need Your Help to Fix It.
          </TypographyRegistrationFormSubTitle>
        </Box>
      </BoxRegistrationSubWrapper>
      <Box sx={{ zIndex: 999 }}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <FormFieldsContainerBox>
              <BoxRegistrationFormTitleMainWrapper>
                <Box>
                  <TypographyRegistrationFormTitleWrapper>
                    Sign Up
                  </TypographyRegistrationFormTitleWrapper>
                </Box>
              </BoxRegistrationFormTitleMainWrapper>
              <Box>
                <Label required>Create your gamer tag </Label>
                <TextFieldRegistrationForm
                  fullWidth
                  type="text"
                  size="small"
                  placeholder="Enter your GamerTag"
                  {...getFieldProps('username')}
                  error={Boolean(touched.username && errors.username)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SportsEsportsOutlinedIcon sx={{ color: theme.palette.blue14 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {touched.username && errors.username && (
                  <FormHelperTextRegistrationForm>
                    {touched.username && errors.username}
                  </FormHelperTextRegistrationForm>
                )}
              </Box>

              <Box>
                <Label required>Email</Label>
                <TextFieldRegistrationForm
                  fullWidth
                  type="email"
                  size="small"
                  placeholder="Enter your Email Address"
                  {...getFieldProps('email')}
                  error={Boolean(touched.email && errors.email)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon sx={{ color: theme.palette.blue14 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {touched.email && errors.email && (
                  <FormHelperTextRegistrationForm>
                    {touched.email && errors.email}
                  </FormHelperTextRegistrationForm>
                )}
              </Box>

              <Box>
                <Label required>Password</Label>
                <TextFieldRegistrationForm
                  fullWidth
                  autoComplete="new-password"
                  type={showPassword ? 'text' : 'password'}
                  size="small"
                  placeholder="Enter Password"
                  {...getFieldProps('password')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <KeyOutlinedIcon sx={{ color: theme.palette.blue14 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                          {showPassword ? (
                            <VisibilityOffOutlinedIcon />
                          ) : (
                            <VisibilityOutlinedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(touched.password && errors.password)}
                />
                {touched.password && errors.password && (
                  <FormHelperTextRegistrationForm>
                    {touched.password && errors.password}
                  </FormHelperTextRegistrationForm>
                )}
              </Box>
              <Box>
                <NormalLabel>ADVENTURE PACKS: (Don't Miss Out)</NormalLabel>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    {...getFieldProps('subscribedForAdventurePacks')}
                    value={formik.values.subscribedForAdventurePacks}
                    onChange={formik.handleChange}
                  >
                    <FormControlLabel
                      value
                      control={
                        <CheckboxControl checked={formik.values.subscribedForAdventurePacks} />
                      }
                      label={<TypographyAdventurePacks>ENABLED</TypographyAdventurePacks>}
                      onClick={() => {
                        handleSubscribedClick(true)
                      }}
                    />

                    <FormControlLabel
                      value
                      control={
                        <CheckboxControl checked={!formik.values.subscribedForAdventurePacks} />
                      }
                      label={<TypographyAdventurePacks>DISABLED</TypographyAdventurePacks>}
                      onClick={() => {
                        handleSubscribedClick(false)
                      }}
                    />
                  </RadioGroup>
                </FormControl>
                <TypographyAdventurePacksInfo>
                  Adventure Pack arrive in an email on the next part of the adventure.
                </TypographyAdventurePacksInfo>
              </Box>
              <Box sx={{}}>
                <FormControl
                  component="fieldset"
                  error={formik.touched.ageOver13 && Boolean(formik.errors.ageOver13)}
                >
                  <RadioGroup
                    row
                    {...getFieldProps('ageOver13')}
                    value={formik.values.ageOver13}
                    onChange={formik.handleChange}
                  >
                    <FormControlLabelRegistrationForm
                      onClick={() => {
                        setAgeCheck(false)
                        toast.dismiss()
                        setIsClickDisabled(false)
                      }}
                      value
                      control={<CustomRadio />}
                      label={
                        <TypographyAgeVerification>
                          I confirm I am 14 years of age or older
                        </TypographyAgeVerification>
                      }
                    />
                    <FormControlLabelRegistrationForm
                      onClick={() => handleAgeCheckClick()}
                      value={false}
                      control={<CustomRadio />}
                      label={
                        <TypographyAgeVerification>
                          I am under 14 years of age.
                        </TypographyAgeVerification>
                      }
                    />
                  </RadioGroup>

                  {formik.touched.ageOver13 &&
                    formik.errors.ageOver13 &&
                    typeof formik.errors.ageOver13 === 'string' && (
                      <FormHelperTextRegistrationForm>
                        {formik.errors.ageOver13}
                      </FormHelperTextRegistrationForm>
                    )}
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <ButtonRegistrationFormSignUp
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || !isValid || !dirty || ageCheck}
                >
                  Sign up
                </ButtonRegistrationFormSignUp>

                <BoxRegistrationFormfooterMainWrapper>
                  <TypographyRegistrationFormfooterTitle sx={{ mt: 1 }}>
                    Already have an account.{' '}
                    <Link
                      href={`/signin${
                        router.query.redirectTo
                          ? `?redirectTo=${encodeURIComponent(router.query.redirectTo as string)}`
                          : ''
                      }${
                        router.query.origin
                          ? `?origin=${encodeURIComponent(router.query.origin as string)}`
                          : ''
                      }`}
                      passHref
                    >
                      <LoginLink>Log In</LoginLink>
                    </Link>
                  </TypographyRegistrationFormfooterTitle>
                  <TypographyRegistrationFormfooterSubTitle sx={{ mt: 1 }} variant="body2">
                    * By Registering in, I agree to End the Rig&apos;s&nbsp;
                    <Link href={LegalLinks.TermsAndServices} target="_blank">
                      <LoginLink>Terms of Service</LoginLink>
                    </Link>
                    &nbsp;and&nbsp;
                    <Link href={PROJECT.privacyPolicy} target="_blank">
                      <LoginLink>Privacy Policy</LoginLink>
                    </Link>
                    .
                  </TypographyRegistrationFormfooterSubTitle>
                </BoxRegistrationFormfooterMainWrapper>
              </Box>
            </FormFieldsContainerBox>
          </Form>
        </FormikProvider>
      </Box>

      <BoxCharacterMainWrapper>
        <ImageContainerBox>
          <SignUpImageChar src={registrationPageCharacter} alt="signup" />
        </ImageContainerBox>
      </BoxCharacterMainWrapper>
    </BoxRegistrationMainWrapper>
  )
}

export default RegisterForm
